import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { BsArrowUpCircle, BsX, BsXCircle } from 'react-icons/bs';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  createGuidedSupportSession,
  getSupportQuestions,
  IGuidedSupportOption,
  IGuidedSupportSession,
  ISupportQuestions,
  selectGuidedSupportOption,
} from 'sdk-apogee';
import { useAppContext } from '@contexts/AppContext';
import { useThemeMode } from '@contexts/ThemeModeContext';
import { useGuidedTour } from '@contexts/GuidedTourContext';
import { tours } from '../../tours';
import useLayoutContext from '@hooks/useLayoutContext';

import InputSectionGroup from '../InputSectionGroup';
import { Button, AnimatedHtmlContent } from '@components';
import { openChat } from '@utils';

const MAX_QUESTIONS = 7;
const FALLBACK_QUESTIONS = [
  'Why is my Apple phone or tablet not able to connect?',
  'Turn off Private WiFi Address on Apple devices',
  'If this is your first time on-site, from this device',
  'If you are a visitor on-site',
  'Set up your account while not on site',
  'Connect Gaming Consoles and other devices',
  'How do I retrieve my username and password?',
  'Issues Connecting to Live Chat',
];

const BottomQuickSetupBar = () => {
  const history = useHistory();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const guidedSupportOptionsRef = useRef<HTMLDivElement | null>(null);
  const wasExpandedRef = useRef(false);
  const hasInteractedRef = useRef(false);
  const skipCloseRefocusRef = useRef(false);
  const { isAuthenticated, user } = useAppContext();
  const { resolvedTheme } = useThemeMode();
  const { subdomain, phoneSupport } = useLayoutContext();
  const { startTour } = useGuidedTour();
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [guidedSupportSession, setGuidedSupportSession] = useState<IGuidedSupportSession>();
  const [selectedGuidedSupportOptionId, setSelectedGuidedSupportOptionId] = useState<number>();
  const [hasSelectedGuidedSupportOption, setHasSelectedGuidedSupportOption] = useState(false);

  const isDark = resolvedTheme === 'dark';
  const supportRoute = isAuthenticated ? '/faq' : '/support';
  const hasActiveGuidedSupportSession = !!guidedSupportSession && !guidedSupportSession.completed;
  const hasStartedGuidedSupportSession =
    hasSelectedGuidedSupportOption ||
    (hasActiveGuidedSupportSession &&
      !!guidedSupportSession.messages?.some(
        (message) => message.selected_option_code_name || (message as any).selectedOptionCodeName,
      ));

  const {
    data: guidedSupportSessionResponse,
    isFetching: isFetchingGuidedSupportSession,
    refetch: refetchGuidedSupportSession,
  } = useQuery<IGuidedSupportSession>({
    queryKey: ['bottom-help-guided-support-session', subdomain, user?.username],
    queryFn: () =>
      createGuidedSupportSession(
        subdomain,
        isAuthenticated && user?.username ? { username: user.username } : undefined,
      ),
    enabled: isExpanded && !!subdomain,
    staleTime: Infinity,
  });

  const { data: supportQuestions = [] } = useQuery<ISupportQuestions[]>({
    queryKey: ['bottom-help-support-questions', subdomain],
    queryFn: () => getSupportQuestions(subdomain),
    enabled: isExpanded && !!subdomain,
  });

  useEffect(() => {
    if (guidedSupportSession) return;
    const session = (guidedSupportSessionResponse as any)?.data || guidedSupportSessionResponse;
    if (session) setGuidedSupportSession(session);
  }, [guidedSupportSessionResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  const flattenedQuestions = useMemo(() => {
    const questions = supportQuestions
      .flatMap((item) => item.questionsAndAnswers || [])
      .map(({ question }) => question)
      .filter((question): question is string => !!question?.trim());

    return questions.length ? questions : FALLBACK_QUESTIONS;
  }, [supportQuestions]);

  const filteredQuestions = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    const questions = flattenedQuestions.slice(0, 40);

    if (!normalizedSearch) {
      return questions.slice(0, MAX_QUESTIONS);
    }

    return questions
      .filter((question) => question.toLowerCase().includes(normalizedSearch))
      .slice(0, MAX_QUESTIONS);
  }, [flattenedQuestions, searchText]);

  const commonQuestions = useMemo(
    () => flattenedQuestions.slice(0, MAX_QUESTIONS),
    [flattenedQuestions],
  );

  useEffect(() => {
    // Distinguishes a real user (keyboard, mouse, touch, or assistive-tech
    // gesture) reaching this field from iOS's Captive Network Assistant,
    // which silently auto-focuses the page's only input with no prior
    // interaction at all. Any genuine interaction — including AT navigation
    // like VoiceOver swipes, which fire touch events without a synthetic
    // Tab keydown — permanently marks the session as user-driven.
    const markInteracted = () => {
      hasInteractedRef.current = true;
      document.removeEventListener('keydown', markInteracted);
      document.removeEventListener('pointerdown', markInteracted);
      document.removeEventListener('touchstart', markInteracted);
    };

    document.addEventListener('keydown', markInteracted);
    document.addEventListener('pointerdown', markInteracted);
    document.addEventListener('touchstart', markInteracted);

    return () => {
      document.removeEventListener('keydown', markInteracted);
      document.removeEventListener('pointerdown', markInteracted);
      document.removeEventListener('touchstart', markInteracted);
    };
  }, []);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isExpanded]);

  useEffect(() => {
    if (wasExpandedRef.current && !isExpanded) {
      // Closing normally (Escape, close button) returns focus to the toggle.
      // Shift+Tab out of the input is the exception: focus is already on its way
      // back into the page, so pulling it here would trap the user in the bar.
      if (skipCloseRefocusRef.current) {
        skipCloseRefocusRef.current = false;
      } else {
        toggleButtonRef.current?.focus();
      }
    }
    wasExpandedRef.current = isExpanded;
  }, [isExpanded]);

  const triggerWifiTour = useCallback(() => {
    setIsExpanded(false);
    startTour(tours.connectWifi.id);
  }, [startTour]);

  const triggerWiredEthernetTour = useCallback(() => {
    setIsExpanded(false);
    startTour(tours.connectWired.id);
  }, [startTour]);

  const triggerCreateAccountTour = useCallback(() => {
    setIsExpanded(false);
    startTour(tours.createOrLogin.id);
  }, [startTour]);

  const latestMessage = useMemo(() => {
    const messages = guidedSupportSession?.messages;
    if (!messages?.length) return undefined;
    return messages.reduce((max, msg) => (msg.id > max.id ? msg : max), messages[0]);
  }, [guidedSupportSession]);

  const guidedSupportOptions = useMemo(() => latestMessage?.options || [], [latestMessage]);

  const guidedSupportContent = useMemo(
    () => (latestMessage?.content || '').replace(/Hello Not Provided,?/gi, 'Hello,'),
    [latestMessage],
  );

  useEffect(() => {
    if (!isExpanded) return;
    if (document.activeElement !== document.body) return;

    const nextFocusTarget =
      guidedSupportOptionsRef.current?.querySelector<HTMLElement>('button, a[href], input') ||
      closeButtonRef.current;
    nextFocusTarget?.focus();
  }, [latestMessage, isExpanded]);

  const driveGuidedSupportMessage = useCallback(
    (title: string, description: string) => {
      setIsExpanded(false);
      startTour(tours.createOrLogin.id, [{ title, description }]);
    },
    [startTour],
  );

  const handleGuidedSupportOption = useCallback(
    async (option: IGuidedSupportOption) => {
      const codeName = (option as any).codeName || option.code_name;
      let selectedSession: IGuidedSupportSession | undefined;

      if (subdomain) {
        setHasSelectedGuidedSupportOption(true);
        setSelectedGuidedSupportOptionId(option.id);
        try {
          const response = await selectGuidedSupportOption(subdomain, option.id);
          selectedSession = (response as any)?.data || response;

          if (
            selectedSession?.transferred_to_agent ||
            (selectedSession as any)?.transferredToAgent
          ) {
            setIsExpanded(false);
            openChat();
          }

          if (selectedSession?.completed) {
            setHasSelectedGuidedSupportOption(false);
            const refetchResponse = await refetchGuidedSupportSession();
            const nextSession = (refetchResponse.data as any)?.data || refetchResponse.data;

            setGuidedSupportSession(nextSession);
            return;
          }

          setGuidedSupportSession(selectedSession);
        } finally {
          setSelectedGuidedSupportOptionId(undefined);
        }
      }

      const msgs = selectedSession?.messages;
      const latestMsg = msgs?.length
        ? msgs.reduce((max, msg) => (msg.id > max.id ? msg : max), msgs[0])
        : undefined;
      const description = latestMsg?.content || option.content;

      const popoverOverride = [{ title: option.content, description }];

      switch (codeName) {
        case 'select_action_create_or_log_into_account':
          // setIsExpanded(false);
          // startTour(tours.createOrLogin.id, popoverOverride);
          break;
        case 'select_action_connect_to_wifi_network':
          // driveGuidedSupportMessage(option.content, description);
          break;
        case 'select_action_connect_to_wired_network':
          // driveGuidedSupportMessage(option.content, description);
          break;
        case 'select_action_chat_with_live_agent':
          setIsExpanded(false);
          openChat();
          break;
        default:
          // driveGuidedSupportMessage(option.content, description);
          break;
      }
    },
    [driveGuidedSupportMessage, refetchGuidedSupportSession, startTour, subdomain],
  );

  const callSupport = useCallback(() => {
    document.location.href = `tel:${(phoneSupport || '').split('-').join('')}`;
  }, [phoneSupport]);

  const panelBackground = isDark ? '#353535' : '#f1f1f1';
  const borderColor = isDark ? '#ffffff' : '#1492ff';
  const primaryText = isDark ? '#ffffff' : '#1f2937';
  const secondaryText = isDark ? '#d4d4d4' : '#4b5563';
  const dividerColor = isDark ? 'rgba(255,255,255,0.75)' : 'rgba(20,146,255,0.45)';

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 mx-4 mb-3 sm:mx-6 lg:mx-10 pointer-events-none">
      <div
        ref={containerRef}
        className="mx-auto flex w-full max-w-screen-2xl items-end gap-3 pointer-events-auto"
      >
        <div className="relative min-w-0 flex-1">
          <InputSectionGroup
            role="search"
            aria-label="Help and quick setup"
            className="bottom-quick-setup-group min-w-0 flex-1"
            onClick={() => {
              setIsExpanded(true);
              window.requestAnimationFrame(() => inputRef.current?.focus());
            }}
            style={{
              backgroundColor: isDark ? '#353535' : '#d8d8d8',
              borderColor,
              boxShadow: '0 -4px 18px rgba(0, 0, 0, 0.22)',
            }}
          >
            <InputSectionGroup.Item className="bottom-quick-setup-group__item">
              <div className="input-section-group__field bottom-quick-setup-group__field">
                <div className="input-section-group__control bottom-quick-setup-group__control">
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchText}
                    readOnly={!isExpanded}
                    aria-label="Search help topics"
                    aria-controls="guided-support-options"
                    className={classNames(
                      'block w-full border-0 py-2 px-2 text-sm leading-5 focus:border-black focus:outline-none',
                      'input-section-group__inline-input bottom-quick-setup-group__input',
                    )}
                    style={{ color: isExpanded ? primaryText : secondaryText }}
                    placeholder={isExpanded ? '' : 'Need help connecting?'}
                    onFocus={(event) => {
                      if (!hasInteractedRef.current) return;
                      // Clear readOnly on the DOM node directly (not just via
                      // React state) so assistive tech querying state right at
                      // focus time doesn't announce "read only" before the
                      // re-render catches up.
                      event.currentTarget.readOnly = false;
                      setIsExpanded(true);
                    }}
                    onKeyDown={(event) => {
                      // Shift+Tab off the field means "I'm leaving" — dismiss the
                      // panel and let focus continue back into the page.
                      if (event.key === 'Tab' && event.shiftKey && isExpanded) {
                        skipCloseRefocusRef.current = true;
                        setIsExpanded(false);
                      }
                    }}
                    onChange={(event) => setSearchText(event.target.value)}
                  />
                  {isExpanded && (
                    <span role="status" className="sr-only">
                      Help panel expanded
                    </span>
                  )}
                </div>
                <button
                  ref={toggleButtonRef}
                  type="button"
                  aria-label={isExpanded ? 'Collapse help panel' : 'Expand help panel'}
                  aria-expanded={isExpanded}
                  aria-controls="guided-support-options"
                  className="shrink-0"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsExpanded((prev) => !prev);
                  }}
                >
                  <BsArrowUpCircle
                    className={`text-3xl transition-transform duration-200 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                    style={{ color: isDark ? '#ffffff' : '#0f7fe8' }}
                  />
                </button>
              </div>
            </InputSectionGroup.Item>
          </InputSectionGroup>

          {/*
            The panel is rendered after the input bar so keyboard order follows
            the visual order (input → toggle → panel contents). It is absolutely
            positioned, so moving it in the DOM does not change where it appears.
          */}
          <div
            role="region"
            aria-label="Guided support"
            aria-hidden={!isExpanded}
            className="bottom-help-panel-anchor absolute inset-x-0 overflow-scroll rounded-[28px] text-left"
            style={{
              backgroundColor: panelBackground,
              border: `1px solid ${dividerColor}`,
              bottom: 'calc(100% + 12px)',
              maxHeight: 'calc(100vH - 100px)',
              boxShadow: '0 -4px 18px rgba(0, 0, 0, 0.22)',
              opacity: isExpanded ? 1 : 0,
              visibility: isExpanded ? 'visible' : 'hidden',
              pointerEvents: isExpanded ? 'auto' : 'none',
              transform: isExpanded ? 'translateY(0) scaleY(1)' : 'translateY(12px) scaleY(0.96)',
              transformOrigin: 'bottom center',
              transition: 'opacity 180ms ease, transform 180ms ease, visibility 0s 180ms',
            }}
          >
            <div className="flex justify-end px-8 pt-6">
              <button
                ref={closeButtonRef}
                type="button"
                aria-label="Close help panel"
                className={`shrink-0 rounded focus-visible:[outline-style:auto] ${
                  isDark ? 'focus-visible:outline-white' : 'focus-visible:outline-[#0f7fe8]'
                }`}
                onClick={() => setIsExpanded(false)}
              >
                <BsX
                  className="text-3xl -mr-5 -mt-3"
                  style={{ color: isDark ? '#ffffff' : '#0f7fe8' }}
                />
              </button>
            </div>
            <div className="flex flex-col gap-8 px-8 pb-8 pt-2 md:grid-cols-[1.3fr_0.9fr]">
              {!hasStartedGuidedSupportSession && (
                <div className="space-y-5">
                  {(filteredQuestions.length ? filteredQuestions : commonQuestions).map(
                    (question) => (
                      <button
                        key={question}
                        type="button"
                        className={`block w-full truncate rounded text-left text-[18px] font-semibold focus-visible:[outline-style:auto] ${
                          isDark ? 'focus-visible:outline-white' : 'focus-visible:outline-[#0f7fe8]'
                        }`}
                        style={{ color: primaryText }}
                        onClick={() => {
                          setIsExpanded(false);
                          history.push(supportRoute, { expandQuestion: question });
                        }}
                      >
                        {question}
                      </button>
                    ),
                  )}
                </div>
              )}

              <div className="space-y-4" id="guided-support-options" ref={guidedSupportOptionsRef}>
                <div aria-live="polite" aria-atomic="false">
                  <AnimatedHtmlContent
                    key={guidedSupportContent}
                    html={guidedSupportContent}
                    className="text-lg"
                    showLoadingDots={hasStartedGuidedSupportSession}
                  />
                </div>
                <div className="flex flex-wrap justify-stretch gap-4">
                  {isFetchingGuidedSupportSession && (
                    <Button disabled loading>
                      Loading
                    </Button>
                  )}
                  {guidedSupportOptions.map((option) => (
                    <Button
                      key={option.id}
                      disabled={!!selectedGuidedSupportOptionId}
                      loading={selectedGuidedSupportOptionId === option.id}
                      onClick={() => handleGuidedSupportOption(option)}
                    >
                      {option.content}
                    </Button>
                  ))}
                  {phoneSupport && (
                    <Button onClick={callSupport}>{`Call Support: ${phoneSupport}`}</Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BottomQuickSetupBar;
